if (!customElements.get('goose-yellowstone-landing')) {
  customElements.define('goose-yellowstone-landing', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.resizers = [];
      this.querySelectorAll('[data-gwy-carousel]').forEach(carousel => {
        const track = carousel.querySelector('[data-gwy-track]');
        const prev = carousel.querySelector('[data-gwy-prev]');
        const next = carousel.querySelector('[data-gwy-next]');
        const move = direction => {
          const card = track.querySelector('[data-gwy-card]');
          if (!card) return;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        };
        const update = () => {
          prev.disabled = track.scrollLeft < 2;
          next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 3;
        };
        prev.addEventListener('click', () => move(-1), options);
        next.addEventListener('click', () => move(1), options);
        track.addEventListener('scroll', update, { ...options, passive: true });
        track.addEventListener('keydown', event => {
          if (event.target !== track) return;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            move(event.key === 'ArrowLeft' ? -1 : 1);
          }
        }, options);
        const resize = new ResizeObserver(update);
        resize.observe(track);
        this.resizers.push(resize);
        update();
      });
      const reviews = this.querySelector('[data-gwy-reviews]');
      const toggle = reviews.querySelector('[data-gwy-reviews-toggle]');
      const marquee = reviews.querySelector('[data-gwy-review-marquee]');
      const reviewGroup = marquee.querySelector('.gwy-review-group');
      const reviewSpeed = Number(reviews.dataset.gwyReviewSpeed) || 50;
      const updateReviewSpeed = () => {
        const pixelsPerSecond = reviewSpeed * (matchMedia('(max-width: 749px)').matches ? 0.8 : 1);
        marquee.style.setProperty('--gwy-review-duration', `${reviewGroup.getBoundingClientRect().width / pixelsPerSecond}s`);
      };
      const reviewResize = new ResizeObserver(updateReviewSpeed);
      reviewResize.observe(reviewGroup);
      this.resizers.push(reviewResize);
      updateReviewSpeed();
      toggle.addEventListener('click', () => {
        const paused = reviews.dataset.paused !== 'true';
        reviews.dataset.paused = String(paused);
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.resume : toggle.dataset.pause);
        toggle.textContent = paused ? '▶' : 'Ⅱ';
      }, options);
      reviews.addEventListener('touchstart', event => {
        if (event.target.closest('[data-gwy-reviews-toggle]')) return;
        reviews.dataset.paused = 'true';
        toggle.setAttribute('aria-pressed', 'true');
        toggle.setAttribute('aria-label', toggle.dataset.resume);
        toggle.textContent = '▶';
      }, { ...options, passive: true });
      this.dialog = this.querySelector('.gwy-video-dialog');
      this.closeButton = this.querySelector('[data-gwy-close]');
      this.mediaSlot = this.querySelector('[data-gwy-dialog-media]');
      this.videoPool = this.querySelector('[data-gwy-video-pool]');
      this.players = new Map();
      const feedback = this.querySelector('[data-gwy-video-feedback]');
      const status = this.querySelector('[data-gwy-video-status]');
      const retry = this.querySelector('[data-gwy-video-retry]');
      const fallback = this.querySelector('[data-gwy-video-fallback]');
      const setState = state => {
        feedback.hidden = state === 'ready';
        status.textContent = state === 'error' ? feedback.dataset.error : feedback.dataset.loading;
        retry.hidden = fallback.hidden = state !== 'error';
        this.mediaSlot.setAttribute('aria-busy', String(state === 'loading'));
      };
      const smallSource = () => matchMedia('(max-width: 749px)').matches || navigator.connection?.saveData || ['slow-2g', '2g', '3g'].includes(navigator.connection?.effectiveType);
      const chooseSource = video => {
        const source = smallSource() ? video.dataset.gwySmallSrc : video.dataset.gwyLargeSrc;
        if (source) video.src = source;
      };
      const play = video => {
        video.preload = 'auto';
        setState(video.readyState >= 2 ? 'ready' : 'loading');
        fallback.href = video.src || video.querySelector('source')?.src || '';
        video.play().catch(error => {
          if (this.activeVideo !== video || error.name === 'AbortError') return;
          setState(error.name === 'NotAllowedError' ? 'ready' : 'error');
        });
      };
      const prepare = film => {
        if (this.players.has(film)) return this.players.get(film);
        const video = film.querySelector('[data-gwy-video]').content.querySelector('video').cloneNode(true);
        chooseSource(video);
        video.preload = 'metadata';
        video.hidden = true;
        const ready = () => { if (this.activeVideo === video) setState('ready'); };
        ['loadeddata', 'canplay', 'playing'].forEach(type => video.addEventListener(type, ready, options));
        ['waiting', 'stalled'].forEach(type => video.addEventListener(type, () => {
          if (this.activeVideo === video && !video.paused) setState('loading');
        }, options));
        video.addEventListener('error', () => { if (this.activeVideo === video) setState('error'); }, options);
        this.players.set(film, video);
        this.videoPool.append(video);
        video.load();
        return video;
      };
      const fitVideo = this.querySelector('[data-gwy-fit-video]');
      if ('IntersectionObserver' in window && !navigator.connection?.saveData) {
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            if (entry.target.matches('.gwy-film')) prepare(entry.target);
            else {
              chooseSource(entry.target);
              entry.target.preload = 'metadata';
              entry.target.load();
            }
            this.videoObserver.unobserve(entry.target);
          });
        }, { rootMargin: '400px 0px' });
        this.querySelectorAll('.gwy-film').forEach(film => this.videoObserver.observe(film));
        if (fitVideo) this.videoObserver.observe(fitVideo);
      }
      ['pointerover', 'focusin', 'pointerdown'].forEach(type => this.addEventListener(type, event => {
        const open = event.target.closest('[data-gwy-open]');
        if (open) prepare(open.closest('.gwy-film')).preload = 'auto';
      }, options));
      this.addEventListener('click', event => {
        const open = event.target.closest('[data-gwy-open]');
        if (open) {
          const video = prepare(open.closest('.gwy-film'));
          this.querySelectorAll('video').forEach(other => other.pause());
          this.returnFocus = open;
          this.activeVideo = video;
          video.hidden = false;
          this.mediaSlot.replaceChildren(video);
          this.dialog.showModal();
          play(video);
          this.closeButton.focus();
        }
        if (event.target.closest('[data-gwy-video-retry]') && this.activeVideo) {
          const video = this.activeVideo;
          if (video.dataset.gwySmallSrc) video.src = video.dataset.gwySmallSrc;
          video.load();
          play(video);
        }
        if (event.target.closest('[data-gwy-close]')) this.dialog.close();
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        const video = this.activeVideo;
        if (video) {
          video.pause();
          if (video.readyState > 0) video.currentTime = 0;
          video.preload = 'metadata';
          video.hidden = true;
          this.videoPool.append(video);
        }
        this.activeVideo = null;
        setState('ready');
        this.returnFocus?.focus({ preventScroll: true });
      }, options);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.resizers?.forEach(resize => resize.disconnect());
      this.videoObserver?.disconnect();
      this.players?.forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      this.players?.clear();
      this.videoPool?.replaceChildren();
    }
  });
}
